<script setup>
import AppAlert from '@/components/atoms/AppAlert'

defineProps({
  // A heading above the copy; Figma draws a titled tip without the icon.
  title: { type: String, default: '' },
  // Keeps the icon beside the heading, as the Orders page's tip does.
  icon: { type: Boolean, default: false },
  // Numbered points under the heading.
  list: { type: Array, default: () => [] },
  // Under a guide step the heading is 12px, not 14px.
  compact: { type: Boolean, default: false },
  // Grey instead of blue: a note, not advice.
  neutral: { type: Boolean, default: false },
  // A help page; the whole tip links to it.
  to: { type: [String, Object], default: null },
  // info: advice, in blue · warning: a caution, in yellow (a CDP Hub article's "Important" box).
  tone: { type: String, default: 'info', validator: (tone) => ['info', 'warning'].includes(tone) },
})

const ICONS = { info: 'heroicons-outline:information-circle', warning: 'heroicons-outline:exclamation-circle' }
</script>

<template>
  <AppAlert
    :variant="tone"
    :icon="title && !icon ? '' : ICONS[tone]"
    static
    :bordered="!neutral"
    :class="[neutral ? 'border border-gray-200 !bg-gray-50' : '', { 'relative z-10 pr-10': to }]"
  >
    <!-- Figma's heading is gray-900 and sits 8px above the copy. -->
    <template v-if="title" #title>
      <span class="mb-1.5 block text-gray-900" :class="{ 'text-sm': !compact }" v-text="title" />
    </template>

    <!-- Figma's tip has gray copy; AppAlert's is gray-900, and it has no grey fill. -->
    <!-- pre-line keeps a blank line in the copy as a paragraph break. -->
    <span class="whitespace-pre-line" :class="neutral ? 'text-gray-600' : 'text-gray-700'"><slot /></span>

    <!-- AppAlert's copy is a paragraph, which cannot hold a list; its action slot is the place under it. -->
    <template v-if="list.length || to" #action>
      <!-- Markers sit outside, so a wrapped line hangs under the text. -->
      <ol v-if="list.length" class="list-decimal space-y-2 pl-4 text-xs" :class="neutral ? 'text-gray-600' : 'text-gray-700'">
        <li v-for="line in list" :key="line" v-text="line" />
      </ol>

      <!-- A linked tip is one link with a chevron on its right; z-10 lifts it over a guide step's own link. -->
      <template v-if="to">
        <RouterLink
          :to="to"
          :aria-label="title || undefined"
          class="absolute inset-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
        />
        <Icon icon="heroicons-solid:chevron-right" class="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-tint-1" />
      </template>
    </template>
  </AppAlert>
</template>
